import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Button
} from '@mui/material';
import { TextFormatting, FlowRule } from '../types/FormTypes';

/**
 * =============================
 * Form Engine Type Definitions
 * =============================
 */

export interface FormField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: {
    pattern?: string;
    message?: string;
  };
  options?: Array<{
    value: string;
    label: string;
  }>;
  conditions?: Array<{
    field: string;
    operator: string;
    value: string | boolean;
  }>;
  fieldStatement?: string;
  imageWidth?: string;
  imageHeight?: string;
  altText?: string;
  value?: string;
}

export interface FormStep {
  id: string;
  title: string;
  description?: string;
  descriptionFormat?: TextFormatting;
  displayStatement?: string;
  displayStatementFormat?: TextFormatting;
  fields: FormField[];
  flowRules?: FlowRule[];
  completionType?: 'none' | 'submit' | 'restart';
}

export interface FormConfig {
  title: string;
  description: string;
  steps: FormStep[];
}

export interface FormattedField {
  question: string;
  answer: string;
}

export interface FormattedSection {
  section: string;
  fields: FormattedField[];
}

export interface FormEngineProps {
  steps: string | Array<FormStep>; 
  formId?: string;
  isExpanded?: boolean;
  onSubmit?: (formData: Record<string, any>, formattedData?: Array<FormattedSection>) => void;
  disableSubmit?: boolean;
  userData?: {
    name?: string;
    email?: string;
    organization?: string;
  };
  formTitle?: string;
  formDescription?: string;
}

/**
 * =============================
 * Form Configuration Parser
 * =============================
 */

/**
 * Parse form steps from various input formats
 */
export const parseFormConfig = (
  rawSteps: string | Array<FormStep> | FormConfig | any,
  customFormTitle?: string,
  customFormDescription?: string
): {
  steps: FormStep[];
  title: string;
  description: string;
} => {
  let parsedSteps: FormStep[] = [];
  let configTitle = customFormTitle || '';
  let configDescription = customFormDescription || '';

  try {
    if (typeof rawSteps === 'string') {
      const parsed = JSON.parse(rawSteps) as any;
      
      if (parsed.steps) {
        // Handle full FormConfig object in JSON string
        parsedSteps = parsed.steps;
        if (!customFormTitle) configTitle = parsed.title || '';
        if (!customFormDescription) configDescription = parsed.description || '';
      } else if (Array.isArray(parsed)) {
        // Handle array of steps in JSON string
        parsedSteps = parsed;
      }
    } else if (Array.isArray(rawSteps)) {
      // Handle array of steps directly
      parsedSteps = rawSteps;
    } else if (rawSteps && typeof rawSteps === 'object') {
      // Handle form config object
      if ('steps' in rawSteps) {
        parsedSteps = rawSteps.steps;
        if (!customFormTitle) configTitle = rawSteps.title || '';
        if (!customFormDescription) configDescription = rawSteps.description || '';
      }
    }
  } catch (e) {
    console.error('Failed to parse form steps:', e);
    parsedSteps = [];
  }

  return {
    steps: parsedSteps,
    title: configTitle,
    description: configDescription
  };
};

/**
 * Validate a field based on its configuration
 */
export const validateField = (field: FormField, value: any): string => {
  // Handle null/undefined fields gracefully
  if (!field) return 'Invalid field configuration';

  // Required field validation
  if (field.required && (value === undefined || value === null || value === '')) {
    return `${field.label || 'Field'} is required`;
  }

  // Email field validation
  if (field.type === 'email' && value && value !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(String(value))) {
      return `Please enter a valid email address`;
    }
  }

  // Pattern validation - with error handling for invalid regex
  if (field.validation?.pattern && value) {
    try {
      const pattern = new RegExp(field.validation.pattern);
      if (!pattern.test(String(value))) {
        return field.validation.message || `Invalid ${field.label || 'input'}`;
      }
    } catch (err) {
      console.error(`Invalid regex pattern for field ${field.id}:`, err);
      return 'Validation error occurred';
    }
  }

  return '';
};

/**
 * Determine if a field should be displayed based on its conditions
 */
export const shouldShowField = (field: FormField, formData: Record<string, any>): boolean => {
  if (!field.conditions || field.conditions.length === 0) {
    return true;
  }

  return field.conditions.every((condition: {field: string, operator: string, value: string | boolean}) => {
    const fieldValue = formData[condition.field];
    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'notEquals':
        return fieldValue !== condition.value;
      case 'contains':
        return String(fieldValue).includes(String(condition.value));
      case 'notContains':
        return !String(fieldValue).includes(String(condition.value));
      default:
        return true;
    }
  });
};

/**
 * Format form data for submission with proper question-answer pairs
 */
export const formatFormDataForSubmission = (
  formData: Record<string, any>,
  parsedSteps: FormStep[]
): Array<FormattedSection> => {
  // Create an array to hold sections (one per step)
  const formattedData: Array<FormattedSection> = [];
  
  // Process each step that has relevant data
  parsedSteps.forEach(step => {
    const fieldsWithData: Array<FormattedField> = [];
    
    // Process each field in the step
    step.fields.forEach((field: FormField) => {
      const value = formData[field.id];
      
      // Skip fields without data (unless it's a checkbox which defaults to false)
      if (value === undefined && field.type !== 'checkbox') return;
      
      // Format the value based on field type
      let displayValue: string;
      if (field.type === 'checkbox') {
        displayValue = value ? 'Yes' : 'No';
      } else if (field.type === 'radio' || field.type === 'select') {
        const option = field.options?.find((opt: {value: string, label: string}) => opt.value === value);
        displayValue = option ? option.label : String(value || '');
      } else {
        displayValue = String(value || '');
      }
      
      // Add to fields array with question and answer
      fieldsWithData.push({
        question: field.label,
        answer: displayValue
      });
    });
    
    // Only add steps that have fields with data
    if (fieldsWithData.length > 0) {
      formattedData.push({
        section: step.title || 'Untitled Section',
        fields: fieldsWithData
      });
    }
  });
  
  return formattedData;
};

/**
 * =============================
 * Form Field Renderer Component
 * =============================
 */

interface FormFieldRendererProps {
  field: FormField;
  value: any;
  onChange: (fieldId: string, value: any) => void;
  error?: string;
}

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  field,
  value,
  onChange,
  error
}) => {
  // Helper function to add asterisk to required field labels
  const getFieldLabel = (label: string, required?: boolean) => {
    return required ? `${label} *` : label;
  };

  // Helper function to wrap field with statement if present
  const wrapWithStatement = (component: React.ReactNode) => {
    const fieldStatementElement = field.fieldStatement ? (
      <Box sx={{ mb: 1 }}>
        {(() => {
          // Safely split the fieldStatement with defensive coding
          const lines = (field.fieldStatement || "").split('\n');
          return lines.map((line, i) => (
            <Typography
              key={i}
              variant="body2"
              color="info.main"
              sx={{
                mb: i < lines.length - 1 ? 0.5 : 0,
                fontStyle: 'italic',
                display: 'block'
              }}
            >
              {line}
            </Typography>
          ));
        })()}
      </Box>
    ) : null;

    return (
      <Box>
        {fieldStatementElement}
        {component}
      </Box>
    );
  };

  switch (field.type) {
    case 'select':
      return wrapWithStatement(
        <FormControl fullWidth error={!!error}>
          <InputLabel>{getFieldLabel(field.label, field.required)}</InputLabel>
          <Select
            value={value || ''}
            label={getFieldLabel(field.label, field.required)}
            onChange={(e) => onChange(field.id, e.target.value)}
          >
            {field.options?.map((option: {value: string, label: string}) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'radio':
      return wrapWithStatement(
        <FormControl error={!!error}>
          <FormLabel>{getFieldLabel(field.label, field.required)}</FormLabel>
          <RadioGroup
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
          >
            {field.options?.map((option: {value: string, label: string}) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'checkbox':
      return wrapWithStatement(
        <FormControl error={!!error}>
          <FormControlLabel
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) => onChange(field.id, e.target.checked)}
              />
            }
            label={getFieldLabel(field.label, field.required)}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'textarea':
      return wrapWithStatement(
        <TextField
          fullWidth
          multiline
          rows={4}
          label={getFieldLabel(field.label, field.required)}
          placeholder={field.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error}
          helperText={error}
        />
      );

    case 'date':
      return wrapWithStatement(
        <TextField
          fullWidth
          type="date"
          label={getFieldLabel(field.label, field.required)}
          InputLabelProps={{ shrink: true }}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error}
          helperText={error}
        />
      );

    case 'imageUrl':
      // For imageUrl type, if there's a value, just show the image
      if (value) {
        return wrapWithStatement(
          <Box>
            <Typography variant="subtitle1" gutterBottom>{getFieldLabel(field.label, field.required)}</Typography>
            <Box mt={2} textAlign="center">
              <img
                src={value}
                alt={field.altText || field.label}
                style={{
                  maxWidth: field.imageWidth ? `${field.imageWidth}px` : '100%',
                  maxHeight: field.imageHeight ? `${field.imageHeight}px` : 'auto',
                  objectFit: 'contain',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  padding: '8px'
                }}
                onError={(e) => {
                  console.error(`Failed to load image: ${value}`);
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.appendChild(
                    Object.assign(document.createElement('div'), {
                      textContent: `Failed to load image: ${value}`,
                      style: 'color: red; font-size: 0.875rem; margin-top: 8px;'
                    })
                  );
                }}
              />
            </Box>
          </Box>
        );
      } else {
        // If no value, show the text input with onBlur instead of onChange
        return wrapWithStatement(
          <Box>
            <TextField
              fullWidth
              type="url"
              label={getFieldLabel(field.label, field.required)}
              placeholder={field.placeholder || "Enter image URL"}
              value={value || ''}
              onChange={(e) => {
                onChange(field.id, e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onChange(field.id, (e.target as HTMLInputElement).value);
                }
              }}
              error={!!error}
              helperText={error || "Enter a URL to an image and press Enter"}
            />
            
            {value && (
              <Button 
                size="small" 
                variant="contained" 
                onClick={() => {
                  onChange(field.id, value);
                }}
                sx={{ mt: 1 }}
              >
                Load Image
              </Button>
            )}
          </Box>
        );
      }

    default:
      return wrapWithStatement(
        <TextField
          fullWidth
          type={field.type}
          label={getFieldLabel(field.label, field.required)}
          placeholder={field.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error}
          helperText={error}
        />
      );
  }
};

/**
 * Renders a form field based on its type
 */
export const renderFormField = (
  field: FormField,
  value: any,
  onChange: (id: string, value: any) => void,
  error: string | null,
  formData: Record<string, any> = {}
): JSX.Element => {
  // Helper function to add asterisk to required field labels
  const getFieldLabel = (label: string, required?: boolean) => {
    return required ? `${label} *` : label;
  };

  // Function to wrap field with statement if needed
  const wrapWithStatement = (component: JSX.Element) => {
    if (field.fieldStatement) {
      return (
        <Box>
          {component}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              mt: 0.5, 
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap'  // This ensures line breaks are preserved
            }}
          >
            {field.fieldStatement}
          </Typography>
        </Box>
      );
    }
    return component;
  };

  switch (field.type) {
    case 'select':
      return wrapWithStatement(
        <FormControl fullWidth error={!!error}>
          <InputLabel>{getFieldLabel(field.label, field.required)}</InputLabel>
          <Select
            value={value || ''}
            label={getFieldLabel(field.label, field.required)}
            onChange={(e) => onChange(field.id, e.target.value)}
          >
            {field.options?.map((option: {value: string, label: string}) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'radio':
      return wrapWithStatement(
        <FormControl error={!!error}>
          <FormLabel>{getFieldLabel(field.label, field.required)}</FormLabel>
          <RadioGroup
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
          >
            {field.options?.map((option: {value: string, label: string}) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'checkbox':
      return wrapWithStatement(
        <FormControl error={!!error}>
          <FormControlLabel
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) => onChange(field.id, e.target.checked)}
              />
            }
            label={getFieldLabel(field.label, field.required)}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'textarea':
      return wrapWithStatement(
        <TextField
          fullWidth
          multiline
          rows={4}
          label={getFieldLabel(field.label, field.required)}
          placeholder={field.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error}
          helperText={error}
        />
      );

    case 'date':
      return wrapWithStatement(
        <TextField
          fullWidth
          type="date"
          label={getFieldLabel(field.label, field.required)}
          InputLabelProps={{ shrink: true }}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error}
          helperText={error}
        />
      );

    case 'imageUrl':
      // For imageUrl type, if there's a value, just show the image
      if (value) {
        return wrapWithStatement(
          <Box>
            <Typography variant="subtitle1" gutterBottom>{getFieldLabel(field.label, field.required)}</Typography>
            <Box mt={2} textAlign="center">
              <img
                src={value}
                alt={field.altText || field.label}
                style={{
                  maxWidth: field.imageWidth ? `${field.imageWidth}px` : '100%',
                  maxHeight: field.imageHeight ? `${field.imageHeight}px` : 'auto',
                  objectFit: 'contain',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  padding: '8px'
                }}
                onError={(e) => {
                  console.error(`Failed to load image: ${value}`);
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement?.appendChild(
                    Object.assign(document.createElement('div'), {
                      textContent: `Failed to load image: ${value}`,
                      style: 'color: red; font-size: 0.875rem; margin-top: 8px;'
                    })
                  );
                }}
              />
            </Box>
          </Box>
        );
      } else {
        // If no value, show the text input with onBlur instead of onChange
        return wrapWithStatement(
          <Box>
            <TextField
              fullWidth
              type="url"
              label={getFieldLabel(field.label, field.required)}
              placeholder={field.placeholder || "Enter image URL"}
              value={value || ''}
              onChange={(e) => {
                onChange(field.id, e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onChange(field.id, (e.target as HTMLInputElement).value);
                }
              }}
              error={!!error}
              helperText={error || "Enter a URL to an image and press Enter"}
            />
            
            {value && (
              <Button 
                size="small" 
                variant="contained" 
                onClick={() => {
                  onChange(field.id, value);
                }}
                sx={{ mt: 1 }}
              >
                Load Image
              </Button>
            )}
          </Box>
        );
      }

    default:
      return wrapWithStatement(
        <TextField
          fullWidth
          type={field.type}
          label={getFieldLabel(field.label, field.required)}
          placeholder={field.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error}
          helperText={error}
        />
      );
  }
}; 